import { Injectable, inject } from '@angular/core';
import { ReactiveApiBrokerService } from 'ngx-mt-ui-lib/api-client';
import { ISelectItem } from 'ngx-mt-ui-lib/interfaces';
import {
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  auditTime,
  defer,
  distinctUntilChanged,
  filter,
  retry,
  scan,
} from 'rxjs';
import {
  ProtocolWorkPage,
  ProtocolWorkRequest,
} from '@core/interfaces/protocol-work.interface';
import {
  ProtocolWorkStreamFrame,
  ProtocolWorkStreamQuery,
  ProtocolWorkStreamState,
} from '@core/interfaces/protocol-work-stream.interface';
import { applyProtocolWorkFrame } from './protocol-work-stream';

/** Private STOMP snapshots and deltas; no HTTP reads or interval reconciliation. */
@Injectable({ providedIn: 'root' })
export class ProtocolWorkStreamService {
  private readonly broker = inject(ReactiveApiBrokerService);
  public readonly online = this.broker.online$;

  public observe(
    query: ProtocolWorkStreamQuery,
  ): Observable<ProtocolWorkStreamState> {
    return defer(() =>
      this.broker
        .watchParsed<ProtocolWorkStreamFrame>(
          `/app/protocol-work/${crypto.randomUUID()}`,
          { 'protocol-work-query': JSON.stringify(query) },
        )
        .pipe(
          scan(applyProtocolWorkFrame, null as ProtocolWorkStreamState | null),
          filter((state): state is ProtocolWorkStreamState => state !== null),
          distinctUntilChanged(),
        ),
    ).pipe(retry({ count: 1, resetOnSuccess: true }));
  }

  public view(): ProtocolWorkView {
    return new ProtocolWorkView((query) => this.observe(query));
  }
}

interface PageSlot {
  result: ReplaySubject<ProtocolWorkPage>;
  producer: Subscription;
  state: ProtocolWorkStreamState | null;
}

/** Owns only the grid's retained windows; evicted blocks release their socket subscriptions. */
export class ProtocolWorkView {
  private readonly pages = new Map<number, PageSlot>();
  private readonly updates = new Subject<void>();
  private readonly roleUpdates = new ReplaySubject<ISelectItem[]>(1);
  private signature: string | null = null;
  public readonly changed = this.updates.pipe(auditTime(50));
  public readonly roles = this.roleUpdates.asObservable();

  public constructor(
    private readonly observe: (
      query: ProtocolWorkStreamQuery,
    ) => Observable<ProtocolWorkStreamState>,
  ) {}

  public load(request: ProtocolWorkRequest): Observable<ProtocolWorkPage> {
    const offset = request.cursor === null ? 0 : Number(request.cursor);
    if (!Number.isSafeInteger(offset) || offset < 0)
      throw new Error('Invalid assignment offset');
    const signature = JSON.stringify({ ...request, cursor: null });
    if (this.signature !== signature) {
      this.clear();
      this.signature = signature;
    }
    const existing = this.pages.get(offset);
    if (existing) return existing.result.asObservable();
    const slot: PageSlot = {
      result: new ReplaySubject(1),
      producer: new Subscription(),
      state: null,
    };
    this.pages.set(offset, slot);
    slot.producer.add(
      this.observe({
        offset,
        limit: request.limit,
        sort: request.sort,
        descending: request.descending,
        filter: request.filter,
      }).subscribe({
        next: (state) => {
          if (this.pages.get(offset) !== slot) return;
          const previous = slot.state;
          slot.state = state;
          this.roleUpdates.next(
            state.roles.map((role) => ({ key: role.id, value: role.name })),
          );
          slot.result.next({
            items: state.items,
            totalElements: state.totalElements,
            nextCursor:
              offset + state.items.length < state.totalElements
                ? String(offset + state.items.length)
                : null,
          });
          if (previous) this.updates.next();
        },
        error: (error) => slot.result.error(error),
      }),
    );
    return slot.result.asObservable();
  }

  public retain(offsets: readonly number[]): void {
    const active = new Set(offsets);
    for (const [offset, slot] of this.pages)
      if (!active.has(offset)) {
        this.pages.delete(offset);
        slot.producer.unsubscribe();
        slot.result.complete();
      }
  }
  public refresh(): void {
    this.clear();
    this.updates.next();
  }
  public dispose(): void {
    this.clear();
    this.updates.complete();
    this.roleUpdates.complete();
  }
  private clear(): void {
    this.retain([]);
    this.signature = null;
  }
}
